/**
 * RelativeDatePicker 组件
 * 是带有日历 和 事件数据的, 用于选择相对时间范围的公共组件,
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from "umi";
import {ListItem, PopoverProps} from 'tea-component';
import {LANGUAGE_EN} from "@home/_config/config";
import RangePicker, {IRangePickerRef} from '@home/components/RangePicker';
import {getLocaleState} from "@home/utils/state";
import {DATALAB_DATE_EVENT} from "@dbl/core/global";
import type { TDatalabDateEvent } from '@dbl/core/global';
import RelativeTimeOptions from "@/pages/datalab/components/DateComponents/RelativeTimeOptions";
import DateEventShortcut, { IQuickValue } from "@/pages/datalab/components/DateComponents/DateEventShortcut";
import {
  calculatePreviousPeriod,
  getDateRange,
  getStartOrEndDate,
  DateGranularity,
  TRelativeTime,
  TDateRange,
  MomentFormatMap,
  MomentUnitMap,
  type TDateGranularity,
} from '@dbl/core/date';
import moment, {Moment} from "moment";
import TimeRangeDisplay from "@/pages/datalab/components/DateComponents/TimeRangeDisplay";
import ConfirmAndCancle from "@/pages/datalab/components/ConfirmAndCancle";
import {isEmpty} from "lodash-es";
import classNames from "classnames";
import './index.scss';

const START_OF_DATE = '2020-01-01';

/**
 * 时间值接口
 */
interface ITimeValue {
  range: [Moment, Moment];
  nameCn?: string;
  nameEn?: string;
  amount?: string;
  granularity?: TDateGranularity;
  isSelectedCustom?: boolean;
  quickValue?: IQuickValue;
}

/**
 * 日期事件接口
 */
interface IDateEvent {
  nameCN?: string;
  name?: string;
}

/**
 * onChange 回调 others 参数接口
 */
interface IOnChangeOthers {
  dateEvent?: IDateEvent;
}

/**
 * RelativeDatePicker 组件 Props 接口
 */
interface IRelativeDatePickerProps {
  baseTimeRange: TDateRange;
  timeGranularity: TDateGranularity;
  value?: TRelativeTime;
  onChange?: (value: TRelativeTime) => void;
  mountClassNames?: string;
  // 扩展popoverProps参数控制Popover展示，用于datalab表格全屏模式下修改挂载方式
  popoverProps?: PopoverProps;
}

const RelativeDatePicker = (props: IRelativeDatePickerProps) => {
  const {
    baseTimeRange,
    timeGranularity,
    value,
    onChange,
    // className = '',
    mountClassNames,
    popoverProps,
  } = props;
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const pickerRef = useRef<IRangePickerRef | null>(null);
  const isClickConfirm = useRef(false); // 是否点击了确认按钮
  // 在报表顶部日期筛选器中, RangePicker组件内部设置 点击日期浮窗外部就关闭日期浮窗的规则
  // 所以在日期浮窗内打开对比时间选择器的浮窗(挂载在body中, 不在日期浮窗内部) 就会关闭日期浮窗
  // 所以这里增加了个挂载节点的状态, 当打开对比时间选择器的浮窗时, 将浮窗挂载到内部, 避免点击关闭日期浮窗
  // 这个方案同时无需修改公共RangePicker组件的逻辑
  // 在其他场景调用时, 不传递mountClassNames, 在打开浮窗时不会触发更新状态
  const [mountNode, setMountNode] = useState<Element | null>( null);
  
  const { data: seasonInfo } = (getLocaleState(DATALAB_DATE_EVENT) || {}) as TDatalabDateEvent;
  
  const initTimeValue = useMemo((): ITimeValue => {
    const { last_time: amount, is_custom: isSelectedCustom, granularity, name_cn: nameCn, name_en: nameEn, time_range: timeRange, quick_value: quickValue } = value || {};
    let range: [Moment, Moment];
    if (timeRange && isEmpty(amount)) {
      range = [moment(timeRange[0]), moment(timeRange[1])];
    } else {
      range = getDateRange(baseTimeRange, {amount, granularity, timeGranularity});
    }
    return {
      range,
      nameCn,
      nameEn,
      amount,
      granularity,
      isSelectedCustom,
      quickValue,
    };
  }, [baseTimeRange, timeGranularity, value]);
  
  const [timeValue, setTimeValue] = useState<ITimeValue>(()=>initTimeValue); // setTimeValue 用来触发内部变更
  const [quickValue, setQuickValue] = useState<IQuickValue>(value?.quick_value || {tab: 'common'});
  
  useEffect(() => {
    setTimeValue(initTimeValue);
  }, [initTimeValue]);
  
  const handleConfirm = () => {
    isClickConfirm.current = true;
    const { range, nameCn, nameEn, isSelectedCustom, amount, granularity} = timeValue;
    /**
     * 都不需要额外的 start, end处理
     * Custom 日历或事件快捷 自助选择
     *    hour粒度, 使用 YYYY-MM-DD HH:mm:ss
     *    其他粒度都是 用YYYY-MM-DD,
     *
     * 固定选项快捷方式
     *    hour粒度, 使用 YYYY-MM-DD HH:mm:ss
     *    其他粒度都是 用YYYY-MM-DD
     *
     */
    
    let timeRange;
    if (isEmpty(amount)) {
      // amount没有值, 代表用户是手动操作日历范围选中的对比时间 或者 直接点击事件时间.
      // 这种时间是不会跟随 base time 的变化而变化的,
      // 所以这里要先计算出 timeRange
      // 等到真正请求计算时, 会转换上传接口, 在那里对没有timeRange的对比时间, 再根据选项的last_time 和 granularity 计算出timeRange上传给后端
      // 这样做的好处时, 当用户更改 base time时, 这种动态选项 上传给后端的timeRange永远都是对的, 而无需用户重新选择选项, 或组件更新状态
      timeRange = range.map((date: Moment) => date.format(MomentFormatMap[timeGranularity === DateGranularity.HOUR ? DateGranularity.HOUR : DateGranularity.DAY]));
    }
    
    onChange?.({
      name_cn: nameCn,
      name_en: nameEn,
      is_custom: isSelectedCustom,
      last_time: amount,
      granularity,
      // timeGranularity 粒度为hour时特殊处理, 其他粒度统一处理
      time_range: timeRange,
      quick_value: quickValue,
    });
    pickerRef.current?.close();
  };
  const handleCancel = () => {
    // 关闭弹窗
    pickerRef.current?.close();
  };
  
  return (<RangePicker
    ref={pickerRef}
    className="search-border"
    overlayClassName={classNames('dbd-range-picker-overlay dbl-filter-date-overlay', {
      'dbl-relative-date-picker-overlay-en': timeGranularity === DateGranularity.HOUR && isEn,
      'dbl-relative-date-picker-overlay-cn': timeGranularity === DateGranularity.HOUR  && !isEn,
    })}
    type={timeGranularity === DateGranularity.HOUR ? 'hour' : 'date'}
    // key={type}
    latestDate={{}}
    value={timeValue.range}
    showSelectType={false}
    language={locale === 'zh-CN' ? 'cn' : 'en'}
    setShortcutOption={() => (<DateEventShortcut
      type={'date'}
      startOfTime={START_OF_DATE}
      seasonInfo={seasonInfo}
      quickValue={quickValue}
      changeQuickValue={setQuickValue}
      changeClose={false}
      commonTab={<RelativeTimeOptions
        baseTimeRange={baseTimeRange}
        timeGranularity={timeGranularity}
        {...timeValue}
        onChange={(value) => {
          const {nameCn, nameEn, amount, granularity, isSelectedCustom} = value;
          const range = getDateRange(baseTimeRange, {amount, granularity, timeGranularity});
          setTimeValue({
            range,
            nameCn,
            nameEn,
            amount,
            granularity,
            isSelectedCustom,
          });
        }}
        extraList={(
          <ListItem
            onClick={() => {
              setTimeValue({
                range: calculatePreviousPeriod(baseTimeRange, timeGranularity),
                nameCn: '自定义',
                nameEn: 'Custom',
                amount: undefined,
                granularity: DateGranularity.DAY,
                isSelectedCustom: true,
              });
            }}
            selected={timeValue.isSelectedCustom && (!timeValue.amount)}
          >
            {formatMessage({id: 'dbl_common_pascal_custom'})}
          </ListItem>
        )}
      />}
    />)}
    changeClose={false}
    onChange={(range: [Moment, Moment], type: string, others?: IOnChangeOthers) => {
      const { dateEvent } = others || {};
      let newRange = range;
      if (type === DateGranularity.HOUR && range[0].hour() === 0) {
        const start = getStartOrEndDate(range[0], { granularity: type, isStart: true, unit: MomentUnitMap[DateGranularity.DAY]});
        const end = getStartOrEndDate(range[1], { granularity: type, isStart: false, unit: MomentUnitMap[DateGranularity.DAY]});
        newRange = [start, end];
      }
      setTimeValue({
        range: newRange,
        nameCn: dateEvent?.nameCN || '自定义',
        nameEn: dateEvent?.name || 'Custom',
        amount: undefined,
        granularity: type === DateGranularity.HOUR ? DateGranularity.HOUR : DateGranularity.DAY,
        isSelectedCustom: true,
      });
      // 只要用户手动点击日历, 那么就不能高亮原本选中的事件日期快捷键.
      // 避免用户手动选择的范围和 事件日期快捷的日期范围不一致.
      // !dateEvent 为true, 代表点击了日历修改日期范围
      !dateEvent && setQuickValue({tab: quickValue.tab || 'common'});
    }}
    footerConfig={{
      left: (<ConfirmAndCancle
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />)
    }}
    isReseted={false}
    onOpenChange={(open: boolean) => {
      if (open) {
        mountClassNames && setMountNode(document.querySelector(mountClassNames) as Element);
        isClickConfirm.current = false;
      } else {
        // 关闭浮窗时, 没有点击确认按钮的话, 要将状态重置到初始态.
        if (!isClickConfirm.current) {
          setTimeValue(initTimeValue);
        }
      }
    }}
    label={(<TimeRangeDisplay
      prefix={isEn ? initTimeValue.nameEn : initTimeValue.nameCn}
      timeRange={initTimeValue?.range || []}
      /* timeValue.amount 不存在的时候, 说明是用户使用日历自定义范围, 且base time的粒度是 日周月时是 day的粒度展示, 而不是跟随base time的粒度;  但 hour粒度一直都是按照YYYY-MM-DD HH:ss:dd */
      granularity={(isEmpty(initTimeValue.amount) && (timeGranularity !== DateGranularity.HOUR)) ? DateGranularity.DAY : timeGranularity} />)}
    popupContainer={mountNode}
    popoverProps={popoverProps}
  />);
};

export default RelativeDatePicker;